import { Injectable } from '@angular/core';
import { FetchPolicy } from '@apollo/client';
import { map, switchMap } from 'rxjs/operators';

import { throwOnGraphqlError } from '@app/core';
import {
  TemplatesGQL,
  TemplatesQueryVariables,
  TemplateGQL,
  TemplateQueryVariables,
  CreateTemplateGQL,
  CreateTemplateMutationVariables,
  UpdateTemplateGQL,
  UpdateTemplateMutationVariables,
  DeleteTemplateGQL,
  DeleteTemplateMutationVariables,
  AttributeInput,
  TemplateInput,
  Attribute
} from 'src/generated/graphql.default';
import { omit } from 'lodash';

@Injectable({ providedIn: 'root' })
export class TemplateService {
  constructor(
    private templatesGQL: TemplatesGQL,
    private templateGQL: TemplateGQL,
    private createTemplateGQL: CreateTemplateGQL,
    private updateTemplateGQL: UpdateTemplateGQL,
    private deleteTemplateGQL: DeleteTemplateGQL
  ) {}

  allTemplates(variables: TemplatesQueryVariables) {
    return this.templatesGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.templates)
    );
  }

  template(variables: TemplateQueryVariables) {
    return this.templateGQL.fetch(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.template)
    );
  }

  create(variables: CreateTemplateMutationVariables) {
    return this.createTemplateGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.createTemplate)
    );
  }

  update(variables: UpdateTemplateMutationVariables) {
    return this.updateTemplateGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.updateTemplate)
    );
  }

  delete(variables: DeleteTemplateMutationVariables) {
    return this.deleteTemplateGQL.mutate(variables).pipe(
      throwOnGraphqlError(),
      map(response => response.data.deleteTemplate)
    );
  }

  duplicate(id: string, newName: string) {
    return this.template({ id }).pipe(
      switchMap(template => {
        const templateParams = { ...omit(template, '__typename', 'id', 'attributes', 'user', 'updated_at', 'created_at') } as TemplateInput;
        templateParams.name = newName;

        const attributesParams = template.attributes.map(
          (attr: Attribute) =>
            ({
              ...omit(
                attr,
                'filled_by.defined_signer_config.__typename',
                'filled_by.defined_signer_config.configs.__typename',
                'filled_by.__typename',
                'style.__typename',
                '__typename',
                'id',
                'position'
              ),
              position: parseInt(attr.position)
            }) as AttributeInput
        );

        return this.create({
          template: templateParams,
          attributes: attributesParams
        });
      })
    );
  }
}
